import { supabase } from '@/lib/supabase';

/**
 * Comments Service - Handles all comment-related operations with Supabase
 */

export const commentsService = {
  /**
   * Get all comments, optionally sorted
   * @param {string} sortBy - Sort field (e.g., '-created_date' for descending)
   * @returns {Promise<Array>}
   */
  async list(sortBy = '-created_date') {
    const ascending = !sortBy.startsWith('-');
    const field = sortBy.replace('-', '');
    
    const { data, error } = await supabase
      .from('comments')
      .select('*')
      .order(field, { ascending });

    if (error) throw error;
    return data || [];
  },

  /**
   * Filter comments by criteria
   * @param {Object} filters - Filter criteria (e.g., { report_id: 'id', user_email: 'email' })
   * @param {string} sortBy - Sort field
   * @returns {Promise<Array>}
   */
  async filter(filters = {}, sortBy = '-created_date') {
    const ascending = !sortBy.startsWith('-');
    const field = sortBy.replace('-', '');
    
    let query = supabase.from('comments').select('*');

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });

    // Apply sorting
    query = query.order(field, { ascending });

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  /**
   * Get a single comment by ID
   * @param {string} id - Comment ID
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const { data, error } = await supabase
      .from('comments')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Create a new comment
   * @param {Object} commentData - Comment data { report_id, user_email, user_name, content }
   * @returns {Promise<Object>}
   */
  async create(commentData) {
    const { data, error } = await supabase
      .from('comments')
      .insert([{
        ...commentData,
        created_date: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update a comment
   * @param {string} id - Comment ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>}
   */
  async update(id, updates) {
    const { data, error } = await supabase
      .from('comments')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete a comment
   * @param {string} id - Comment ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    const { error } = await supabase
      .from('comments')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};
